import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'
import { Button } from './Button'
import { Dialog } from './Dialog'
import { TextField } from './Fields'

export interface ConfirmOptions {
  title: string
  message: ReactNode
  confirmLabel: string
  /** Default "Annulla". */
  cancelLabel?: string | undefined
  /** Azione distruttiva: la conferma usa `tone="error"` (unico uso ammesso sui bottoni). */
  destructive?: boolean | undefined
}

export interface PromptOptions {
  title: string
  /** Testo esplicativo sopra il campo. */
  message?: ReactNode | undefined
  /** Etichetta del campo (sempre visibile). Con `required` riceve l'asterisco. */
  label: string
  initialValue?: string | undefined
  /** Il bottone di conferma resta disattivo finche' il valore, tolti gli spazi, e' vuoto. */
  required?: boolean | undefined
  multiline?: boolean | undefined
  confirmLabel: string
  /** Default "Annulla". */
  cancelLabel?: string | undefined
  /** Azione distruttiva: la conferma usa `tone="error"`. */
  destructive?: boolean | undefined
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>
/** Risolve con la stringa digitata (anche vuota, non tagliata, come window.prompt) o `null` se annullato. */
type PromptFn = (options: PromptOptions) => Promise<string | null>

const ConfirmContext = createContext<ConfirmFn | null>(null)
const PromptContext = createContext<PromptFn | null>(null)

/**
 * Sostituisce `window.confirm()`: `if (!(await confirm({ title, message, confirmLabel }))) return`.
 * Risolve true alla conferma, false con Annulla, Esc o chiusura. Il focus iniziale e' sulla conferma, o su Annulla se `destructive`.
 * Richiede <SnackbarProvider>, che monta l'host del dialog.
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm richiede <SnackbarProvider>')
  return ctx
}

/**
 * Sostituisce `window.prompt()`: `const reason = await prompt({ title, label, confirmLabel })`; `null` = annullato.
 * Dialog `sm` come modulo con un TextField; stesso host (e stessa coda) di useConfirm.
 */
export function usePrompt(): PromptFn {
  const ctx = useContext(PromptContext)
  if (!ctx) throw new Error('usePrompt richiede <SnackbarProvider>')
  return ctx
}

type Pending =
  | { id: number; kind: 'confirm'; options: ConfirmOptions; resolve: (ok: boolean) => void }
  | { id: number; kind: 'prompt'; options: PromptOptions; resolve: (value: string | null) => void }

function PromptDialog({
  item,
  open,
  onSettle,
}: {
  item: Extract<Pending, { kind: 'prompt' }>
  open: boolean
  onSettle: (value: string | null) => void
}) {
  const o = item.options
  const [value, setValue] = useState(o.initialValue ?? '')
  const blocked = Boolean(o.required) && value.trim() === ''
  return (
    <Dialog
      open={open}
      onClose={() => onSettle(null)}
      title={o.title}
      size="sm"
      as="form"
      onSubmit={(e) => {
        e.preventDefault()
        if (!blocked) onSettle(value)
      }}
      actions={
        <>
          <Button variant="text" type="button" onClick={() => onSettle(null)}>
            {o.cancelLabel ?? 'Annulla'}
          </Button>
          <Button variant="text" type="submit" tone={o.destructive ? 'error' : 'primary'} disabled={blocked}>
            {o.confirmLabel}
          </Button>
        </>
      }
    >
      <div className="d-flex flex-column gap-3">
        {o.message ? <div className="md-dialog__message">{o.message}</div> : null}
        <TextField
          label={o.required ? `${o.label} *` : o.label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          multiline={Boolean(o.multiline)}
          required={Boolean(o.required)}
        />
      </div>
    </Dialog>
  )
}

/** Montato da SnackbarProvider: un dialog alla volta, le richieste successive vanno in coda. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Pending[]>([])
  const [open, setOpen] = useState(false)
  const nextId = useRef(0)
  // Il contenuto resta disponibile durante l'animazione di uscita.
  const lastItem = useRef<Pending | null>(null)

  const confirm = useCallback<ConfirmFn>((options) => {
    return new Promise<boolean>((resolve) => {
      setQueue((q) => [...q, { id: ++nextId.current, kind: 'confirm', options, resolve }])
      setOpen(true)
    })
  }, [])

  const prompt = useCallback<PromptFn>((options) => {
    return new Promise<string | null>((resolve) => {
      setQueue((q) => [...q, { id: ++nextId.current, kind: 'prompt', options, resolve }])
      setOpen(true)
    })
  }, [])

  const current = queue[0]
  if (current) lastItem.current = current

  const advance = () => {
    setQueue((q) => q.slice(1))
    // Con altre richieste in coda il dialog resta aperto sul prossimo contenuto.
    if (queue.length <= 1) setOpen(false)
  }
  const settleConfirm = (ok: boolean) => {
    if (!current || current.kind !== 'confirm') return
    current.resolve(ok)
    advance()
  }
  const settlePrompt = (value: string | null) => {
    if (!current || current.kind !== 'prompt') return
    current.resolve(value)
    advance()
  }

  const item = current ?? lastItem.current
  const isOpen = open && Boolean(current)

  return (
    <ConfirmContext.Provider value={useMemo(() => confirm, [confirm])}>
      <PromptContext.Provider value={useMemo(() => prompt, [prompt])}>
        {children}
        {item?.kind === 'confirm' ? (
          <Dialog
            open={isOpen}
            onClose={() => settleConfirm(false)}
            title={item.options.title}
            size="sm"
            actions={
              <>
                <Button variant="text" data-autofocus={item.options.destructive ? '' : undefined} onClick={() => settleConfirm(false)}>
                  {item.options.cancelLabel ?? 'Annulla'}
                </Button>
                <Button variant="text" tone={item.options.destructive ? 'error' : 'primary'} data-autofocus={item.options.destructive ? undefined : ''} onClick={() => settleConfirm(true)}>
                  {item.options.confirmLabel}
                </Button>
              </>
            }
          >
            <div className="md-dialog__message">{item.options.message}</div>
          </Dialog>
        ) : null}
        {item?.kind === 'prompt' ? <PromptDialog key={item.id} item={item} open={isOpen} onSettle={settlePrompt} /> : null}
      </PromptContext.Provider>
    </ConfirmContext.Provider>
  )
}
